import * as THREE from "three";
import { Engine } from "../../game/engine";
import { AudioBed } from "../../game/audio";

/**
 * Pulse — rhythm-tap timing, redesigned for 3D depth + engagement.
 * Game 01 of 12. (v2 — feel-pass sweep.)
 *
 * Scoring contract preserved: seed-driven beat schedule + tap events
 * (idx, delta_ms) replay unchanged. Visual layer rebuilt: rings approach
 * the camera through depth, hit zone is a fixed bright torus, beat-locked
 * environment lighting, thunder beats every 8th.
 */

export const RUN_S = 60;
export const BPM_START = 90;
export const BPM_END = 140;
export const HIT_WINDOW_MS = 400;
export const SWING = 0.6;
export const LEAD_MS = 1100;
const SPAWN_Z = -28;
const HIT_Z = 4;
// Replaced by CHORD_EVERY_CLIENT defined below — chord beats are
// mechanically meaningful (double-tap required) rather than cosmetic.

export interface PulseEvent { idx: number; delta_ms: number }

/** v4 — section names matching scoring-pulse SECTION_NAMES. */
export type Section = "verse" | "drop" | "outro";
const SECTION_BOUNDARIES_MS_CLIENT = [0, 20_000, 45_000];
function sectionForClient(t_ms: number): Section {
  if (t_ms >= SECTION_BOUNDARIES_MS_CLIENT[2]) return "outro";
  if (t_ms >= SECTION_BOUNDARIES_MS_CLIENT[1]) return "drop";
  return "verse";
}
const FINISHER_THRESHOLDS_CLIENT = [10, 20, 30];

export interface PulseCallbacks {
  onHud: (idx: number, totalBeats: number, lastBucket: string, scoreEstimate: number, section: Section, comboStreak: number) => void;
  onGameOver: (result: { events: PulseEvent[]; scoreEstimate: number; bestCombo: number; bucketsByName: Record<string, number>; chordsHit: number; finishersEarned: number; section: Section }) => void;
  onSection?: (section: Section) => void;
  onFinisher?: (level: number) => void;
}

function makeRng(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface Beat { idx: number; t_target_ms: number }

function generateBeats(seed: number): Beat[] {
  const rng = makeRng(seed);
  const out: Beat[] = [];
  let t = 0;
  let idx = 0;
  while (t < RUN_S * 1000) {
    const u = Math.min(1, t / (RUN_S * 1000));
    const bpm = BPM_START + (BPM_END - BPM_START) * u;
    const beatMs = 60_000 / bpm;
    let target = t;
    if (idx % 2 === 1) {
      target += SWING * (beatMs / 2) * (rng() - 0.5);
    } else {
      rng();
    }
    out.push({ idx, t_target_ms: Math.round(target) });
    t += beatMs;
    idx += 1;
  }
  return out;
}

interface RingMesh {
  beat: Beat;
  mesh: THREE.Mesh;
  mat: THREE.MeshStandardMaterial;
  haloMat: THREE.MeshBasicMaterial;
  halo: THREE.Mesh;
  /** Number of taps registered on this ring (0..2 for chord, 0..1 otherwise). */
  taps: number;
  /** Chord beat (every 7th from idx 7) — accepts a second tap for bonus. */
  chord: boolean;
  /** Extra inner rings on chord beats — only allocated when chord=true. */
  innerRings?: THREE.Mesh[];
}

const CHORD_EVERY_CLIENT = 7;
const CHORD_BONUS_CLIENT = 400;

export class Pulse {
  private engine: Engine;
  private audio = new AudioBed();
  private beats: Beat[];
  private rings: RingMesh[] = [];
  private hitTorus: THREE.Mesh;
  private hitTorusMat: THREE.MeshStandardMaterial;
  private hitHalo: THREE.Mesh;
  private hitHaloMat: THREE.MeshBasicMaterial;
  private startedAt = 0;
  private currentBeat = 0;
  private events: PulseEvent[] = [];
  private streak = 0;
  private bestStreak = 0;
  private chordsHit = 0;
  // v4 — section + finisher state.
  private section: Section = "verse";
  private finishersEarned = 0;
  private nextFinisherIdx = 0;
  private scoreEstimate = 0;
  private buckets: Record<string, number> = { perfect: 0, great: 0, good: 0, miss: 0 };
  private done = false;
  private cb: PulseCallbacks;
  /** v5(c): per-player input-lag compensation. Subtracted from the raw
   *  tap time before binary-searching the beat. Server doesn't know about
   *  it — calibration is self-defeating as an inflator (dossier §05.3.2).
   *  Sourced from the 3-tap auto-calibration wizard or the advanced
   *  manual slider. Both write to `pulse.tap_offset_ms` in localStorage. */
  private tapOffsetMs = 0;
  private envLight: THREE.PointLight;
  private flashPulse = 0;
  // v5(b): audio-reactive nebula tinting. fogColor + sceneBgColor are
  // refs cached at construction so the per-frame BPM-phase driver can
  // mutate them without an extra Three-side allocation.
  private fogColor!: THREE.Color;
  private sceneBgColor!: THREE.Color;

  private onKey = (e: KeyboardEvent) => {
    if (e.code === "Space") { e.preventDefault(); this.handleTap(); }
  };
  private onPointer = (_e: PointerEvent) => this.handleTap();

  constructor(
    canvas: HTMLCanvasElement,
    cb: PulseCallbacks,
    seed = Math.floor(Math.random() * 0x7fffffff),
    opts: { tapOffsetMs?: number } = {},
  ) {
    this.cb = cb;
    this.beats = generateBeats(seed);
    // v5(c): clamp the loaded offset to a reasonable input-lag window so
    // a poisoned localStorage value can't be used as a score inflator.
    // Server-side hit-window stays the ground truth; this is just a UX clamp.
    if (typeof opts.tapOffsetMs === "number" && Number.isFinite(opts.tapOffsetMs)) {
      this.tapOffsetMs = Math.max(-150, Math.min(150, opts.tapOffsetMs));
    }

    this.engine = new Engine(canvas, { onFrame: (dt, t) => this.frame(dt, t) });
    this.engine.camera.position.set(0, 0.5, 14);
    this.engine.camera.lookAt(0, 0, -5);
    this.engine.camera.fov = 60;
    this.engine.camera.updateProjectionMatrix();
    this.engine.scene.fog = new THREE.Fog(0x040614, 12, 35);
    this.engine.scene.background = new THREE.Color(0x040614);
    // v5(b): keep refs so the BPM-phase driver in frame() can mutate the
    // existing color objects in place (no per-frame allocations).
    this.fogColor = (this.engine.scene.fog as THREE.Fog).color;
    this.sceneBgColor = this.engine.scene.background as THREE.Color;

    const ambient = new THREE.AmbientLight(0x4070a0, 0.4);
    this.engine.scene.add(ambient);
    // Beat-pulsed environment light at the hit zone — recolors with thunder beats.
    this.envLight = new THREE.PointLight(0x66ddff, 1.0, 30, 1.5);
    this.envLight.position.set(0, 0, HIT_Z - 1);
    this.engine.scene.add(this.envLight);
    const key = new THREE.DirectionalLight(0xfff0d0, 0.5);
    key.position.set(4, 6, 8);
    this.engine.scene.add(key);

    // Hit zone — a bright torus the rings must align with.
    const hrGeo = new THREE.TorusGeometry(3.2, 0.18, 18, 64);
    this.hitTorusMat = new THREE.MeshStandardMaterial({ color: 0xffffff, emissive: 0x66ddff, emissiveIntensity: 1.3, roughness: 0.2, metalness: 0.5 });
    this.hitTorus = new THREE.Mesh(hrGeo, this.hitTorusMat);
    this.hitTorus.position.set(0, 0, HIT_Z);
    this.engine.scene.add(this.hitTorus);
    // Outer halo behind the torus.
    const haloGeo = new THREE.RingGeometry(3.4, 4.2, 64);
    this.hitHaloMat = new THREE.MeshBasicMaterial({ color: 0x66ddff, transparent: true, opacity: 0.35, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
    this.hitHalo = new THREE.Mesh(haloGeo, this.hitHaloMat);
    this.hitHalo.position.set(0, 0, HIT_Z - 0.4);
    this.engine.scene.add(this.hitHalo);

    // Floor with grid lines for depth read.
    const floorGeo = new THREE.PlaneGeometry(80, 80);
    const floorMat = new THREE.MeshStandardMaterial({ color: 0x06080f, roughness: 0.9, metalness: 0.1 });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -4;
    this.engine.scene.add(floor);
    const gridPts: number[] = [];
    for (let z = -35; z <= 15; z += 3) {
      gridPts.push(-25, -3.99, z, 25, -3.99, z);
    }
    for (let x = -24; x <= 24; x += 4) {
      gridPts.push(x, -3.99, -35, x, -3.99, 15);
    }
    const gridGeo = new THREE.BufferGeometry();
    gridGeo.setAttribute("position", new THREE.Float32BufferAttribute(gridPts, 3));
    const gridMat = new THREE.LineBasicMaterial({ color: 0x66ddff, transparent: true, opacity: 0.18, blending: THREE.AdditiveBlending });
    this.engine.scene.add(new THREE.LineSegments(gridGeo, gridMat));

    canvas.addEventListener("pointerdown", this.onPointer);
    window.addEventListener("keydown", this.onKey);
  }

  start() {
    this.audio.arm();
    this.audio.setDroneIntensity(0.5);
    // v5(a): seed the sustained chime stem silent so the drop's lift is a
    // shaped ramp from zero, not a click-on. Per Frequency dossier §6
    // canonical continuous-modulation API.
    this.audio.setChimeGain(0, 0);
    this.startedAt = performance.now();
    this.currentBeat = 0;
    this.events = [];
    this.streak = 0;
    this.bestStreak = 0;
    this.scoreEstimate = 0;
    this.chordsHit = 0;
    this.buckets = { perfect: 0, great: 0, good: 0, miss: 0 };
    this.section = "verse";
    this.finishersEarned = 0;
    this.nextFinisherIdx = 0;
    this.done = false;
    this.engine.start();
  }

  private handleTap() {
    if (this.done) return;
    // v5(c): subtract the calibrated tap_offset_ms so the recorded delta
    // reflects the player's perceived hit, not their input-chain lag.
    const now = performance.now() - this.startedAt - this.tapOffsetMs;
    // Pick the unconsumed ring closest to its hit window. A chord ring
    // with one tap registered is still eligible for the second tap.
    let bestI = -1;
    let bestDelta = Infinity;
    for (const r of this.rings) {
      const maxTaps = r.chord ? 2 : 1;
      if (r.taps >= maxTaps) continue;
      const delta = now - r.beat.t_target_ms;
      if (Math.abs(delta) <= HIT_WINDOW_MS && Math.abs(delta) < Math.abs(bestDelta)) {
        bestDelta = delta;
        bestI = this.rings.indexOf(r);
      }
    }
    if (bestI < 0) return;
    const r = this.rings[bestI];
    r.taps += 1;
    const delta = bestDelta;
    const bucket = this.bucketize(delta);
    this.buckets[bucket] += 1;
    if (bucket === "miss") {
      this.streak = 0;
      this.audio.thud();
    } else {
      this.streak += 1;
      if (this.streak > this.bestStreak) this.bestStreak = this.streak;
      const detune = bucket === "perfect" ? 12 : bucket === "great" ? 7 : 4;
      this.audio.chime(detune, 0);
      // v4: combo finisher detection.
      while (
        this.nextFinisherIdx < FINISHER_THRESHOLDS_CLIENT.length &&
        this.streak >= FINISHER_THRESHOLDS_CLIENT[this.nextFinisherIdx]
      ) {
        const level = this.nextFinisherIdx + 1;
        this.finishersEarned += 1;
        this.nextFinisherIdx += 1;
        // Estimate matches server bonus order (300/700/1500).
        const bonus = [300, 700, 1500][level - 1] ?? 0;
        this.scoreEstimate += bonus;
        this.cb.onFinisher?.(level);
        // Big arpeggio for the milestone.
        this.audio.chime(24, -0.3);
        this.audio.chime(28, 0);
        this.audio.chime(31, 0.3);
      }
    }
    const weight = bucket === "perfect" ? 500 : bucket === "great" ? 300 : bucket === "good" ? 100 : 0;
    // v4: per-section multiplier on the bucket weight.
    const secMult = this.section === "drop" ? 1.4 : this.section === "outro" ? 1.15 : 1.0;
    const mult = 1 + Math.min(this.streak, 40) * 0.04;
    this.scoreEstimate += Math.round(weight * mult * secMult);
    this.events.push({ idx: r.beat.idx, delta_ms: Math.round(delta) });

    // Chord completion bonus: if both taps landed and neither was a miss.
    if (r.chord && r.taps === 2) {
      // Look up the most recent two events for this beat.
      const evs = this.events.slice(-2).filter((e) => e.idx === r.beat.idx);
      const bothHit = evs.length === 2 && evs.every((e) => this.bucketize(e.delta_ms) !== "miss");
      if (bothHit) {
        this.scoreEstimate += CHORD_BONUS_CLIENT;
        this.chordsHit += 1;
        // Arpeggio fanfare on chord complete.
        this.audio.chime(19, -0.3);
        this.audio.chime(24, 0.3);
      }
    }
    // Flash the hit torus.
    this.flashPulse = 1.0;
    this.cb.onHud(this.events.length, this.beats.length, bucket, this.scoreEstimate, this.section, this.streak);
  }

  private bucketize(deltaMs: number): "perfect" | "great" | "good" | "miss" {
    const a = Math.abs(deltaMs);
    if (a <= 30) return "perfect";
    if (a <= 60) return "great";
    if (a <= 110) return "good";
    return "miss";
  }

  private frame(dt: number, _t: number) {
    if (this.done) return;
    const now = performance.now() - this.startedAt;

    // v4: section transition.
    const sec = sectionForClient(now);
    if (sec !== this.section) {
      this.section = sec;
      this.cb.onSection?.(sec);
      // Lift the drone for the drop, soften it for the outro.
      this.audio.setDroneIntensity(sec === "drop" ? 0.85 : sec === "outro" ? 0.35 : 0.5);
      // v5(a): shaped continuous-modulation lift of the sustained chime
      // stem. The drop's drone lift used to be a stepped boost; routing
      // through setChimeGain gives it an audible 1.2s ramp on the way in
      // and a softer 0.8s descent on the outro. Verse re-entry (mid-run
      // back-step is impossible — only start) ramps from zero.
      const chimeTarget = sec === "drop" ? 0.85 : sec === "outro" ? 0.3 : 0;
      const chimeSmoothMs = sec === "drop" ? 1200 : sec === "outro" ? 800 : 400;
      this.audio.setChimeGain(chimeTarget, chimeSmoothMs);
      // Distinct chime cue per section transition.
      this.audio.chime(sec === "drop" ? 19 : 12, 0);
    }

    // v5(b): audio-reactive nebula tinting. Fog hue follows the BPM
    // phase so the visual breathes with the schedule the player is
    // tapping. We re-derive `bpm` here from `now` the same way
    // generateBeats does (linear ramp 90→140 over RUN_S) and compute the
    // position within the current beat as `phase ∈ [0,1)`. The exponential
    // falloff (`pulse = e^(-phase·6)`) makes lightness spike right after
    // each downbeat and decay before the next one — the fog "breathes" at
    // the same cadence as the rings. Section sets the base hue: cyan in
    // the verse, warm in the drop, gold in the outro.
    const u = Math.min(1, now / (RUN_S * 1000));
    const bpmNow = BPM_START + (BPM_END - BPM_START) * u;
    const beatMs = 60_000 / bpmNow;
    const phase = (now % beatMs) / beatMs;
    const pulse = Math.exp(-phase * 6);
    const baseHue = this.section === "drop" ? 0.04 : this.section === "outro" ? 0.12 : 0.55;
    const lightness = 0.035 + pulse * 0.09;
    this.fogColor.setHSL(baseHue, 0.7, lightness);
    this.sceneBgColor.setHSL(baseHue, 0.7, lightness * 0.55);

    // Spawn rings whose target is within LEAD_MS of now.
    while (this.currentBeat < this.beats.length) {
      const b = this.beats[this.currentBeat];
      if (b.t_target_ms - now > LEAD_MS) break;
      this.spawnRing(b);
      this.currentBeat += 1;
    }

    // Update ring positions: travel from SPAWN_Z to HIT_Z over LEAD_MS.
    for (let i = this.rings.length - 1; i >= 0; i--) {
      const r = this.rings[i];
      const tLeft = (r.beat.t_target_ms - now);
      // u: 1 at spawn (LEAD_MS away), 0 at hit time, negative past.
      const u = tLeft / LEAD_MS;
      const z = HIT_Z + (SPAWN_Z - HIT_Z) * u;
      r.mesh.position.set(0, 0, z);
      r.halo.position.set(0, 0, z - 0.4);
      r.mesh.rotation.z = -tLeft * 0.002 + r.beat.idx;
      // Fade past the hit window.
      const past = now - r.beat.t_target_ms;
      const fade = past > HIT_WINDOW_MS ? Math.max(0, 1 - (past - HIT_WINDOW_MS) / 240) : 1;
      r.mat.emissiveIntensity = (r.chord ? 1.8 : 1.0) * fade;
      r.haloMat.opacity = (r.chord ? 0.55 : 0.4) * fade;
      if (past > HIT_WINDOW_MS + 280) {
        // Count implicit misses for untapped slots: 1 for normal beats,
        // 2 for chord beats fully untapped, 1 for chord beats with 1 tap.
        const maxTaps = r.chord ? 2 : 1;
        const missing = maxTaps - r.taps;
        if (missing > 0) {
          this.buckets.miss += missing;
          this.streak = 0;
        }
        this.engine.scene.remove(r.mesh);
        this.engine.scene.remove(r.halo);
        r.mesh.geometry.dispose();
        r.mat.dispose();
        r.halo.geometry.dispose();
        r.haloMat.dispose();
        if (r.innerRings) {
          for (const ir of r.innerRings) {
            this.engine.scene.remove(ir);
            ir.geometry.dispose();
            (ir.material as THREE.Material).dispose();
          }
        }
        this.rings.splice(i, 1);
      }
    }

    // Hit-torus flash decay + breathing.
    if (this.flashPulse > 0) {
      this.flashPulse = Math.max(0, this.flashPulse - dt * 4);
    }
    const breath = 1 + Math.sin(now * 0.005) * 0.04 + this.flashPulse * 0.25;
    this.hitTorus.scale.setScalar(breath);
    this.hitTorusMat.emissiveIntensity = 1.0 + this.flashPulse * 1.5;
    this.hitHaloMat.opacity = 0.3 + this.flashPulse * 0.5;
    // Environment light pulses with the closest-to-hit ring.
    let nearestU = Infinity;
    let chordNear = false;
    for (const r of this.rings) {
      const maxTaps = r.chord ? 2 : 1;
      if (r.taps >= maxTaps) continue;
      const u = Math.abs(r.beat.t_target_ms - now);
      if (u < nearestU) { nearestU = u; chordNear = r.chord; }
    }
    const proximity = Math.max(0, 1 - nearestU / 400);
    this.envLight.intensity = 0.6 + proximity * 1.6;
    this.envLight.color.setHex(chordNear && proximity > 0.4 ? 0xff5b3a : 0x66ddff);

    // Move chord inner rings along with their main mesh.
    for (const r of this.rings) {
      if (!r.innerRings) continue;
      for (const ir of r.innerRings) ir.position.copy(r.mesh.position);
    }

    if (this.currentBeat >= this.beats.length && this.rings.length === 0 && now > RUN_S * 1000) {
      this.finish();
    }
  }

  private spawnRing(beat: Beat) {
    const chord = beat.idx > 0 && beat.idx % CHORD_EVERY_CLIENT === 0;
    // Main torus.
    const torusGeo = new THREE.TorusGeometry(3.0, chord ? 0.18 : 0.1, 14, 56);
    const mat = new THREE.MeshStandardMaterial({
      color: chord ? 0xff5b3a : 0x66ddff,
      emissive: chord ? 0xff2010 : 0x0080cc,
      emissiveIntensity: chord ? 1.8 : 1.0,
      roughness: 0.3,
      metalness: 0.5,
      transparent: true,
      opacity: 0.95,
    });
    const mesh = new THREE.Mesh(torusGeo, mat);
    // Halo disc behind the torus for far-away visibility.
    const haloGeo = new THREE.RingGeometry(3.2, 4.4, 32);
    const haloMat = new THREE.MeshBasicMaterial({
      color: chord ? 0xff5b3a : 0x66ddff,
      transparent: true,
      opacity: chord ? 0.55 : 0.4,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });
    const halo = new THREE.Mesh(haloGeo, haloMat);
    this.engine.scene.add(mesh);
    this.engine.scene.add(halo);

    // Chord beats get two additional concentric inner rings so the
    // "double-tap" call-to-action reads at a glance.
    const innerRings: THREE.Mesh[] = [];
    if (chord) {
      for (const r of [2.0, 2.5]) {
        const ig = new THREE.TorusGeometry(r, 0.08, 10, 48);
        const im = new THREE.MeshStandardMaterial({
          color: 0xffd54a,
          emissive: 0xff8800,
          emissiveIntensity: 1.4,
          roughness: 0.35,
          metalness: 0.5,
          transparent: true,
          opacity: 0.9,
        });
        const imesh = new THREE.Mesh(ig, im);
        this.engine.scene.add(imesh);
        innerRings.push(imesh);
      }
    }

    this.rings.push({ beat, mesh, mat, halo, haloMat, taps: 0, chord, innerRings: innerRings.length ? innerRings : undefined });
  }

  private finish() {
    this.done = true;
    this.audio.setDroneIntensity(0);
    // v5(a): tail the sustained chime stem out so the post-run silence is
    // a fade, not a cut.
    this.audio.setChimeGain(0, 400);
    this.cb.onGameOver({
      events: this.events,
      scoreEstimate: this.scoreEstimate,
      bestCombo: this.bestStreak,
      bucketsByName: this.buckets,
      chordsHit: this.chordsHit,
      finishersEarned: this.finishersEarned,
      section: this.section,
    });
  }

  setMuted(b: boolean) { this.audio.setMuted(b); }
  setVolume(v: number) { this.audio.setVolume(v); }
  armAudio() { this.audio.arm(); }

  dispose() {
    window.removeEventListener("keydown", this.onKey);
    this.engine.dispose();
  }
}
